import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, InputAdornment, List, ListItemButton,
  ListItemText, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { BookOpen, Download, Plus, Search } from "lucide-react";
import { HelpdeskPage, useHd } from "./HelpdeskContext";
import { crud, hd, errorMessage } from "./api";
import { EmptyState, GlassCard, PageHeader, T, TYPE, formatRelative, ghostButtonSx, labelSx, primaryButtonSx } from "../user/complaints/shared";

const articlesApi = crud("/kb/articles");
const catsApi = crud("/kb/categories");
const STATUS = { draft: ["#475467", "#F2F4F7"], review: ["#B54708", "#FEF0C7"], published: ["#067647", "#DCFAE6"], archived: ["#98A2B3", "#F9FAFB"] };
const TYPES = ["article", "faq", "troubleshooting", "guide", "documentation"];

function KB() {
  const ctx = useHd();
  const manage = ctx.can("knowledge.manage");
  const [cats, setCats] = useState([]);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ categoryId: "", status: "", q: "" });
  const [edit, setEdit] = useState(null);
  const [newCat, setNewCat] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([catsApi.list(), articlesApi.list({ ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) })]);
      setCats(c);
      setRows(a);
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const importFaqs = async () => {
    try {
      const r = await hd.kbImportFaqs(false);
      ctx.notify(r.message);
      load();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      await catsApi.create({ name: newCat.trim() });
      setNewCat("");
      load();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  return (
    <Box>
      <PageHeader
        icon={BookOpen}
        title="Knowledge Base"
        subtitle="Manage self-service articles, FAQs, and guides. Published articles power the customer portal and AI answers."
        actions={manage && (
          <>
            <Button variant="outlined" sx={ghostButtonSx} startIcon={<Download size={15} />} onClick={importFaqs}>Import Solution FAQs</Button>
            <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ title: "", body: "", type: "article", status: "draft", tags: [], aiEnabled: true, portal: false, categoryId: "" })}>New article</Button>
          </>
        )} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "240px minmax(0,1fr)" }, gap: 2 }}>
        <GlassCard sx={{ p: 1, alignSelf: "start" }}>
          <List dense>
            <ListItemButton selected={!filter.categoryId} onClick={() => setFilter((f) => ({ ...f, categoryId: "" }))}><ListItemText primary="All categories" /></ListItemButton>
            {cats.map((c) => <ListItemButton key={c._id} selected={filter.categoryId === c._id} onClick={() => setFilter((f) => ({ ...f, categoryId: c._id }))}><ListItemText primary={c.name} /></ListItemButton>)}
          </List>
          {manage && (
            <Stack direction="row" spacing={1} sx={{ p: 1 }}>
              <TextField size="small" placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
              <Button size="small" onClick={addCat}>Add</Button>
            </Stack>
          )}
        </GlassCard>
        <GlassCard>
          <Stack direction="row" spacing={1} sx={{ p: 2, borderBottom: `1px solid ${T.border}` }}>
            <TextField size="small" placeholder="Search titles" value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }} sx={{ flexGrow: 1 }} />
            <TextField select size="small" label="Status" value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} sx={{ minWidth: 150 }}>
              <MenuItem value="">Any</MenuItem>
              {Object.keys(STATUS).map((s) => <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          {!rows.length ? <EmptyState icon={BookOpen} title="No articles" description="Create articles or import your existing Solution FAQs." /> : (
            <Table size="small">
              <TableHead><TableRow sx={{ "& th": { ...labelSx, bgcolor: T.surfaceHeader } }}><TableCell>Title</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell><TableCell>AI</TableCell><TableCell>Portal</TableCell><TableCell>Updated</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a._id} hover sx={{ cursor: "pointer" }} onClick={() => setEdit({ ...a, categoryId: a.categoryId || "" })}>
                    <TableCell sx={{ fontWeight: 600 }}>{a.title}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{a.type}</TableCell>
                    <TableCell><Chip size="small" label={a.status} sx={{ bgcolor: STATUS[a.status][1], color: STATUS[a.status][0], fontWeight: 600, textTransform: "capitalize" }} /></TableCell>
                    <TableCell>{a.aiEnabled ? "Yes" : "No"}</TableCell>
                    <TableCell>{a.portal ? "Public" : "—"}</TableCell>
                    <TableCell>{formatRelative(a.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </GlassCard>
      </Box>
      {edit && <ArticleDialog article={edit} cats={cats} manage={manage} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Box>
  );
}

function ArticleDialog({ article, cats, manage, onClose, onSaved }) {
  const ctx = useHd();
  const [a, setA] = useState({ ...article, tags: (article.tags || []).join(", ") });
  const [error, setError] = useState("");
  const set = (k) => (e) => setA((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (status) => {
    try {
      const body = { title: a.title, body: a.body, type: a.type, categoryId: a.categoryId || null, tags: a.tags.split(",").map((x) => x.trim()).filter(Boolean), aiEnabled: a.aiEnabled, status: status || a.status, ...(manage ? { portal: Boolean(a.portal) } : {}) };
      if (a._id) await articlesApi.update(a._id, body);
      else await articlesApi.create(body);
      ctx.notify("Article saved");
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const remove = async () => {
    try {
      await articlesApi.remove(a._id);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{a._id ? "Edit article" : "New article"} {a.version ? <Typography component="span" sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>v{a.version}</Typography> : null}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField fullWidth label="Title" value={a.title} onChange={set("title")} disabled={!manage} /></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth label="Type" value={a.type} onChange={set("type")} disabled={!manage}>{TYPES.map((t) => <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>{t}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth label="Category" value={a.categoryId} onChange={set("categoryId")} disabled={!manage}><MenuItem value="">None</MenuItem>{cats.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Tags" value={a.tags} onChange={set("tags")} disabled={!manage} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline minRows={10} label="Content" value={a.body} onChange={set("body")} disabled={!manage} /></Grid>
          <Grid item xs={12}><FormControlLabel control={<Checkbox checked={a.aiEnabled} onChange={set("aiEnabled")} disabled={!manage} />} label="AI assistant may use this article once published" /></Grid>
          <Grid item xs={12} sx={{ mt: -1.5 }}><FormControlLabel control={<Checkbox checked={Boolean(a.portal)} onChange={set("portal")} disabled={!manage} />} label="Show in the customer portal once published" /></Grid>
        </Grid>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Status: <b>{a.status}</b>. Draft → Review → Published → Archived.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        {a._id && manage && <Button color="error" onClick={remove}>Delete</Button>}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        {manage && (
          <>
            <Button onClick={() => save("draft")}>Save draft</Button>
            <Button onClick={() => save("review")}>Send to review</Button>
            {a.status === "published" && <Button onClick={() => save("archived")}>Archive</Button>}
            <Button variant="contained" sx={primaryButtonSx} onClick={() => save("published")}>Publish</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function KnowledgeBase() {
  return <HelpdeskPage><KB /></HelpdeskPage>;
}
